import { useState } from 'react';
import {
  pulseIncidentAck,
  pulseIncidentDetail,
  pulseIncidentNote,
  pulseIncidentResolve,
  pulseIncidentUnack,
  type IncidentDetail,
  type IncidentV3,
  type PulseIncident,
  type PulseIncidentExtra,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { SPEC, chipS, durNoSec, env, pre, usePulseApi } from './lib';
import { incidentExtra } from './incidentExtra';
import { SelfFixes, fixLineOf } from './SelfFixes';
import { ackerName } from './acker';

export type Inc = PulseIncident & PulseIncidentExtra;
export type IncFilter = 'Open' | 'Acked' | 'Resolved' | 'All';

/** The short reference people say ("inc-204"), or the start of the id. */
export const incRef = (i: Inc) => i.ref ?? i.id.slice(0, 8);
/** Which service an incident belongs to (design i[6]): facility, or the signal's first word. */
export const incKind = (i: Inc) => (i.scope === 'facility' ? 'facility' : i.signal.split('-')[0]);
/** critical pages (down); warning surfaces (degraded). */
export const incSev = (i: Inc): 'down' | 'degraded' => (i.severity === 'warning' ? 'degraded' : 'down');
export const effState = (i: Inc, ackedHere: Set<string>) => (i.status === 'acknowledged' || (i.status === 'open' && ackedHere.has(i.id)) ? 'acked' : i.status);

const FILTER_STYLE = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:rgba(79,138,214,.15);color:#2F5C97;font-weight:600;border:1px solid rgba(47,92,151,.34);' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');
const CARD_BTN = 'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.2);';
const CAP = 'display:block;font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;';

/** The Developer acknowledges only runtime and cron incidents (Spec V4 14): the platform pages them. */
const devPage = (i: Inc) => (i.category ? i.category === 'runtime' || i.category === 'cron' : incKind(i) !== 'facility');

/** The Incidents tab (design puIsInc, V4 T8314-8440), with the self-fixes runbook under it. */
export function IncidentsTab({
  incidents,
  v3,
  filter,
  setFilter,
  openId,
  setOpenId,
  ackedHere,
  onAcked,
  canWrite,
  dev = false,
  devCanWrite = false,
  writeTitle,
  now,
}: {
  incidents: Inc[];
  v3: IncidentV3[];
  filter: IncFilter;
  setFilter: (f: IncFilter) => void;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  ackedHere: Set<string>;
  onAcked: (id: string, acked: boolean) => void;
  canWrite: boolean;
  /** The signed-in role is the Developer: facility incidents read "Not your page". */
  dev?: boolean;
  /** The Developer may write (not viewing as): runtime and cron incidents take their ack. */
  devCanWrite?: boolean;
  writeTitle?: string;
  now: number;
}) {
  const { client } = useSession();
  const [err, setErr] = useState<string | null>(null);
  const mayAck = (i: Inc) => canWrite || (dev && devCanWrite && devPage(i));
  const rows = incidents.filter((i) => filter === 'All' || effState(i, ackedHere) === filter.toLowerCase() || (filter === 'Open' && i.status !== 'resolved' && ackedHere.has(i.id)));
  const ackedOpen = incidents.filter((i) => i.status !== 'resolved' && effState(i, ackedHere) === 'acked');
  const emptyHead = filter === 'Open' ? (ackedOpen.length ? 'Nothing open and unacknowledged' : 'No open incident') : filter === 'Acked' ? 'Nothing acknowledged and still open' : 'Nothing resolved in this window';
  const emptyNote =
    filter === 'Open'
      ? ackedOpen.length
        ? 'Everything open has been acknowledged, it is not resolved. ' + ackedOpen.length + (ackedOpen.length === 1 ? ' incident is' : ' incidents are') + ' acked and still open under the Acked filter, and the conditions that opened them are still true.'
        : 'The monitor opens one automatically the moment a beat goes red: an empty queue means every service answered inside its threshold.'
      : filter === 'Acked'
        ? 'An acknowledged incident sits here until it resolves. Nothing is currently in that state.'
        : 'Resolved incidents keep their postmortem trail. None closed inside this window.';

  const ack = (i: Inc, on: boolean) => {
    if (!mayAck(i)) return;
    setErr(null);
    (on ? pulseIncidentAck(client, { params: { id: i.id }, body: { confirm: true } }) : pulseIncidentUnack(client, { params: { id: i.id } })).then(
      () => onAcked(i.id, on),
      (e: unknown) => setErr(e instanceof Error ? e.message : 'The request failed.'),
    );
  };

  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 15px;')} data-testid="pulse-incidents">
      <div style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')} role="tablist" aria-label="Incident filter">
        {(['Open', 'Acked', 'Resolved', 'All'] as IncFilter[]).map((f) => (
          <a key={f} href="#" role="tab" aria-selected={filter === f} onClick={(e) => { pre(e); setFilter(f); }} style={sx(FILTER_STYLE(filter === f))}>
            <span>{f}</span>
          </a>
        ))}
      </div>
      {err && (
        <div role="alert" style={sx('font-size:14px;color:#B3261E;font-weight:700;margin-bottom:8px;')}>
          {err}
        </div>
      )}
      {rows.length === 0 && (
        <div style={sx('box-sizing:border-box;background:rgba(34,39,46,.028);border:1px dashed rgba(34,39,46,.18);border-radius:9px;padding:12px 14px;')}>
          <div style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>
            <span>{emptyHead}</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.45;text-wrap:pretty;')}>
            <span>{emptyNote}</span>
          </div>
        </div>
      )}
      <div style={sx('min-width:0;')}>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:0 16px;align-items:start;min-width:0;')}>
          {rows.map((i) => (
            <IncidentRow
              key={i.id}
              i={i}
              v3={v3.find((x) => x.id === i.id) ?? null}
              doneHere={ackedHere.has(i.id)}
              expanded={openId === i.id}
              onToggle={() => setOpenId(openId === i.id ? null : i.id)}
              onAck={(on) => ack(i, on)}
              canWrite={canWrite}
              canAckRow={mayAck(i)}
              devRO={dev && !devPage(i)}
              writeTitle={writeTitle}
              now={now}
            />
          ))}
        </div>
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:10px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
        <span>Incidents open themselves when a beat goes red - one per condition, so a flapping service cannot storm the queue. Acknowledging halts escalation; the postmortem note trail is append-only.</span>
      </div>
      <SelfFixes />
    </div>
  );
}

function IncidentRow({
  i,
  v3,
  doneHere,
  expanded,
  onToggle,
  onAck,
  canWrite,
  canAckRow,
  devRO,
  writeTitle,
  now,
}: {
  i: Inc;
  v3: IncidentV3 | null;
  doneHere: boolean;
  expanded: boolean;
  onToggle: () => void;
  onAck: (on: boolean) => void;
  canWrite: boolean;
  /** This row's ack is allowed (a Developer acks runtime and cron incidents only). */
  canAckRow: boolean;
  /** A facility incident seen by the Developer: "Not your page · read only", no Acknowledge. */
  devRO: boolean;
  writeTitle?: string;
  now: number;
}) {
  const { session } = useSession();
  const sev = incSev(i);
  const t = SPEC[sev];
  // Resolved by hand in this session (the Flutter console's Resolve, restored).
  const [resolvedHere, setResolvedHere] = useState(false);
  const status = resolvedHere ? 'resolved' : i.status;
  // Acked in this session: the server may already say acknowledged; the undo stays where the tap was.
  const done = doneHere && status !== 'resolved';
  const acked = done || status === 'acknowledged';
  const state = acked ? 'ACKED' : status.toUpperCase();
  const stateColors: [string, string] = acked ? ['#264C7E', 'rgba(79,138,214,.16)'] : status === 'resolved' ? ['#1F6B52', 'rgba(47,138,111,.16)'] : ['#B3261E', 'rgba(179,38,30,.14)'];
  const age = durNoSec((now - Date.parse(i.openedAt)) / 60000);
  const mttr = i.resolvedAt ? durNoSec((Date.parse(i.resolvedAt) - Date.parse(i.openedAt)) / 60000) : null;
  const ackNote = done
    ? 'acknowledged by you · escalation halted'
    : i.acknowledgedAt
      ? 'acked by ' + ackerName(i, session?.user.id) + ' · ' + durNoSec((now - Date.parse(i.acknowledgedAt)) / 60000)
      : i.autoResolved && mttr
        ? 'acked auto-resolved · MTTR ' + mttr
        : 'not acknowledged';
  const inert = !canAckRow;
  const fixLine = fixLineOf(i.fixKey, status === 'resolved' || i.autoResolved);
  return (
    <div style={sx('display:flex;align-items:flex-start;gap:10px;flex-wrap:wrap;row-gap:7px;padding:11px 0;border-top:1px solid rgba(34,39,46,.07);')} data-testid="pulse-incident">
      <span style={sx('flex:1 1 200px;min-width:min(100%,200px);')}>
        <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:5px;')}>
          <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
            <span>{incRef(i)}</span>
          </span>
          <span style={sx('font-size:16px;font-weight:600;')}>
            <span>{i.title}</span>
          </span>
        </span>
        <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;')}>
          open <span>{age}</span> · <span>{ackNote}</span> · <span>{incKind(i)}</span> · paged <span>{i.paged ?? 'on-call'}</span>
        </span>
        {fixLine && (
          <span style={sx('display:block;font-size:14px;color:#3C454F;margin-top:3px;line-height:1.45;text-wrap:pretty;')}>
            <span>{fixLine}</span>
          </span>
        )}
      </span>
      <span style={sx(chipS(t[0], t[1]))}>
        <span>{sev.toUpperCase()}</span>
      </span>
      <span style={sx(chipS(stateColors[0], stateColors[1]))}>
        <span>{state}</span>
      </span>
      {status === 'open' && !done && !devRO && (
        <a href="#" aria-disabled={inert ? 'true' : undefined} title={inert ? writeTitle : undefined} onClick={(e) => { pre(e); onAck(true); }} style={sx('display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;')}>
          Acknowledge
        </a>
      )}
      {status === 'open' && !done && devRO && (
        <span style={sx('display:inline-flex;align-items:center;height:28px;padding:0 10px;border-radius:7px;font-size:14px;color:#54606C;background:rgba(34,39,46,.06);white-space:nowrap;')}>Not your page · read only</span>
      )}
      {done && (
        <a href="#" aria-disabled={inert ? 'true' : undefined} onClick={(e) => { pre(e); onAck(false); }} style={sx(CARD_BTN)}>
          Undo ack · paging resumes
        </a>
      )}
      <a href="#" aria-expanded={expanded} onClick={(e) => { pre(e); onToggle(); }} style={sx(CARD_BTN)}>
        <span>{expanded ? 'Hide detail' : 'Open detail'}</span>
      </a>
      {expanded && <IncidentDetailPanel i={i} v3={v3} done={done} now={now} canWrite={canWrite} writeTitle={writeTitle} resolved={status === 'resolved'} onResolved={() => setResolvedHere(true)} />}
    </div>
  );
}

const REM_STATE = (s: IncidentDetail['remediation']): 'open' | 'in progress' | 'closed' =>
  !s || s.status === 'none' ? 'open' : s.status === 'remediated' || s.status === 'verified' ? 'closed' : 'in progress';

function IncidentDetailPanel({
  i,
  v3,
  done,
  now,
  canWrite,
  writeTitle,
  resolved,
  onResolved,
}: {
  i: Inc;
  v3: IncidentV3 | null;
  done: boolean;
  now: number;
  canWrite: boolean;
  writeTitle?: string;
  resolved: boolean;
  onResolved: () => void;
}) {
  const { client } = useSession();
  const [ask, setAsk] = useState<'resolve' | 'note' | null>(null);
  const d = usePulseApi((c, signal) => env(pulseIncidentDetail(c, { params: { id: i.id }, signal })), [i.id]);
  const det = d.data;
  const ex = incidentExtra(v3, now);
  const mtta = done ? 'just now' : i.acknowledgedAt ? durNoSec((Date.parse(i.acknowledgedAt) - Date.parse(i.openedAt)) / 60000) : i.autoResolved ? 'n/a · auto-resolved before anyone acked' : 'not acknowledged yet';
  const mttr = i.resolvedAt ? durNoSec((Date.parse(i.resolvedAt) - Date.parse(i.openedAt)) / 60000) : 'not resolved yet';
  const rs = REM_STATE(det?.remediation ?? null);
  const remColors: [string, string] = rs === 'closed' ? ['#1F6B52', 'rgba(47,138,111,.14)'] : rs === 'in progress' ? ['#5B6B7C', 'rgba(91,107,124,.11)'] : ['#B3261E', 'rgba(179,38,30,.10)'];
  const steps = (det?.timeline ?? []).map((s) => {
    const kind = s.event === 'acknowledged' ? 'ACKED' : /resolved/.test(s.event) ? 'RESOLVED' : s.event.toUpperCase();
    return { when: '−' + durNoSec((now - Date.parse(s.at)) / 60000), kind, what: s.what ?? s.event, who: !s.by || s.by === 'monitor' ? 'auto' : s.by };
  });
  if (done) steps.push({ when: 'now', kind: 'ACKED', what: 'you · escalation halted', who: 'you' });
  const kc = (k: string) => (k === 'OPENED' || k === 'ESCALATED' ? '#B3261E' : k === 'ACKED' || k === 'RESOLVED' ? '#2F5C97' : null);
  const notes = det?.notes ?? [];
  return (
    <span style={sx('flex:1 1 100%;min-width:100%;box-sizing:border-box;background:rgba(34,39,46,.022);border:1px solid rgba(34,39,46,.1);border-radius:9px;padding:13px 14px;margin-top:4px;')} data-testid="pulse-incident-detail">
      {d.error && <span style={sx('display:block;font-size:14px;color:#B3261E;')}>The incident detail could not be read.</span>}
      <span style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:11px 16px;')}>
        <span style={sx('min-width:0;')}>
          <span style={sx(CAP)}>Opened by</span>
          <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>
            <span>{i.openedBy ?? 'the monitor'}</span>
          </span>
        </span>
        <span style={sx('min-width:0;')}>
          <span style={sx(CAP)}>Linked cause</span>
          <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>
            <span>{i.cause ?? det?.suggestedCause?.reason ?? 'not linked yet'}</span>
          </span>
        </span>
        <span style={sx('min-width:0;')}>
          <span style={sx(CAP)}>MTTA · time to ack</span>
          <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;margin-top:3px;')}>
            <span>{mtta}</span>
          </span>
        </span>
        <span style={sx('min-width:0;')}>
          <span style={sx(CAP)}>MTTR · time to resolve</span>
          <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;margin-top:3px;')}>
            <span>{mttr}</span>
          </span>
        </span>
      </span>
      <span style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:5px;margin-top:13px;padding-top:11px;border-top:1px solid rgba(34,39,46,.09);')}>
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>Remediation</span>
        <span style={sx(chipS(remColors[0], remColors[1]))}>
          <span>{rs.toUpperCase()}</span>
        </span>
        <span style={sx('flex:1 1 180px;min-width:min(100%,180px);font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
          <span>{det?.remediation?.summary ?? 'none yet · nobody has taken this'}</span>
        </span>
      </span>
      {ex && (
        <span style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:12px 20px;margin-top:13px;padding-top:11px;border-top:1px solid rgba(34,39,46,.09);')}>
          <span style={sx('min-width:0;')}>
            <span style={sx(CAP)}>Who it reaches</span>
            <span style={sx('display:block;font-size:14px;color:#22272E;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>
              <span>{ex.blast}</span>
            </span>
          </span>
          <span style={sx('min-width:0;')}>
            <span style={sx(CAP)}>What changed in the hour before</span>
            {ex.changed.map((c, n) => (
              <span key={n} style={sx('display:block;font-size:14px;color:#22272E;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
                <span style={sx('font-variant-numeric:tabular-nums;color:#54606C;')}>
                  <span>{c.when}</span>
                </span>{' '}
                · <span style={sx('font-weight:700;')}><span>{c.kind}</span></span> · <span>{c.what}</span>{' '}
                <span style={sx('color:#54606C;')}>
                  · <span>{c.who}</span>
                </span>
              </span>
            ))}
            <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
              <span>{ex.changedNote}</span>
            </span>
          </span>
          <span style={sx('min-width:0;')}>
            <span style={sx(CAP)}>Runbook</span>
            <span style={sx('display:block;font-size:14px;font-weight:700;margin-top:4px;')}>
              <span>{ex.rbName}</span>
            </span>
            {ex.rbSteps.map((r) => (
              <span key={r.n} style={sx('display:block;font-size:14px;color:#22272E;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>
                <span>{r.n}</span>. <span>{r.t}</span>
              </span>
            ))}
            <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.45;margin-top:4px;')}>
              <span>{ex.rbMeta}</span>
            </span>
          </span>
          <span style={sx('min-width:0;')}>
            <span style={sx(CAP)}>After it closes</span>
            <span style={sx('display:block;font-size:14px;color:#22272E;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>
              <span>{ex.review}</span>
            </span>
            <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>
              <span>{ex.facNote}</span>
            </span>
          </span>
        </span>
      )}
      <span style={sx('display:block;margin-top:13px;padding-top:11px;border-top:1px solid rgba(34,39,46,.09);')}>
        <span style={sx('display:block;font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;margin-bottom:8px;')}>Timeline</span>
        {steps.map((s, n) => {
          const c = kc(s.kind);
          return (
            <span key={n} style={sx('display:grid;grid-template-columns:minmax(0,62px) minmax(0,86px) minmax(0,1fr) minmax(0,110px);gap:5px 11px;align-items:start;padding:5px 0;')}>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
                <span>{s.when}</span>
              </span>
              <span style={sx('display:flex;align-items:flex-start;gap:6px;min-width:0;')}>
                <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;margin-top:4px;background:' + (c ?? 'rgba(34,39,46,.28)') + ';')} />
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.09em;font-weight:700;white-space:nowrap;color:' + (c ?? '#54606C') + ';')}>
                  <span>{s.kind}</span>
                </span>
              </span>
              <span style={sx('font-size:14px;color:#3C454F;line-height:1.45;min-width:0;text-wrap:pretty;')}>
                <span>{s.what}</span>
              </span>
              <span style={sx('font-size:14px;color:#54606C;line-height:1.4;min-width:0;text-wrap:pretty;')}>
                <span>{s.who}</span>
              </span>
            </span>
          );
        })}
      </span>
      <span style={sx('display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;font-size:14px;color:#54606C;margin-top:11px;padding-top:10px;border-top:1px solid rgba(34,39,46,.09);line-height:1.5;text-wrap:pretty;')}>
        <span style={sx('flex:1 1 260px;min-width:0;')}>
          <span>{notes.length ? notes.length + ' postmortem note' + (notes.length === 1 ? '' : 's') + ' · append-only' : 'No postmortem note yet. The trail is append-only, an entry cannot be edited or removed once written.'}</span>
        </span>
      {/* Add a note and Resolve by hand: the Flutter console's incident controls, restored
          in the card's own button style on the note line, so the design's layout keeps its
          height. The design's detail has neither. */}
      <span data-testid="pulse-incident-actions" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
        <a href="#" aria-disabled={!canWrite ? 'true' : undefined} title={!canWrite ? writeTitle : undefined} onClick={(e) => { pre(e); if (canWrite) setAsk('note'); }} style={sx(CARD_BTN)}>
          Add a note
        </a>
        {!resolved && (
          <a href="#" aria-disabled={!canWrite ? 'true' : undefined} title={!canWrite ? writeTitle : undefined} onClick={(e) => { pre(e); if (canWrite) setAsk('resolve'); }} style={sx(CARD_BTN)}>
            Resolve
          </a>
        )}
      </span>
      </span>
      {ask && (
        <ConfirmDialog
          title={ask === 'resolve' ? `Resolve ${incRef(i)}?` : `Add a note to ${incRef(i)}`}
          body={ask === 'resolve' ? 'It closes now, with your note and your name. If the condition is still true, the monitor opens a new incident.' : 'It is added to the postmortem trail with your name. The trail is append-only.'}
          confirmLabel={ask === 'resolve' ? 'Resolve' : 'Add note'}
          reason={{ label: ask === 'resolve' ? 'Resolution note' : 'Note', placeholder: ask === 'resolve' ? 'What fixed it (required)' : 'What you found (required)', required: true }}
          onConfirm={async (note) => {
            if (ask === 'resolve') {
              await pulseIncidentResolve(client, { params: { id: i.id }, body: { note } });
              onResolved();
            } else {
              await pulseIncidentNote(client, { params: { id: i.id }, body: { note, kind: 'note' } });
            }
            d.reload();
          }}
          onClose={() => setAsk(null)}
        />
      )}
    </span>
  );
}
